/**
 * Statement-card editor. Headline + accent phrase + checklist bullets → a live
 * StatementPreview, with an optional AI "message → headline + bullets" path.
 * Only kind="statement" templates are selectable here.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import StatementPreview from "@/components/StatementPreview";
import { Sparkles, RefreshCw, X, Plus, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";

export default function StatementEditor({
  open, onClose, onSaved, variation, campaignId, templates,
}) {
  const statementTemplates = (templates || []).filter((t) => t.kind === "statement");
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [headline, setHeadline] = useState("");
  const [accent, setAccent] = useState("");
  const [bullets, setBullets] = useState([]);
  const [typeSpeed, setTypeSpeed] = useState("medium");
  const [marketingMessage, setMarketingMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (variation) {
      setName(variation.name || "");
      setTemplateId(variation.template_id || statementTemplates[0]?.id || "");
      setHeadline(variation.headline || "");
      setAccent(variation.accent || "");
      setBullets(variation.bullets?.length ? [...variation.bullets] : [""]);
      setTypeSpeed(variation.type_speed || "medium");
      setMarketingMessage(variation.marketing_message || "");
    } else {
      setName("New statement");
      setTemplateId(statementTemplates[0]?.id || "");
      setHeadline("Nine specialized AI agents.");
      setAccent("AI");
      setBullets([
        "Three layers of style context the agents read on every run.",
        "Five deterministic patch guards. Three editing modes.",
      ]);
      setTypeSpeed("medium");
      setMarketingMessage("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, variation, templates]);

  if (!open) return null;
  const template = statementTemplates.find((t) => t.id === templateId) || statementTemplates[0];

  const generate = async () => {
    if (!marketingMessage.trim()) { toast.error("Type a marketing message first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/message-to-statement", { message: marketingMessage });
      setName(data.title || name);
      setHeadline(data.headline || headline);
      setAccent(data.accent || "");
      setBullets(data.bullets?.length ? data.bullets : bullets);
      toast.success("Generated statement");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error("AI failed — " + (typeof d === "string" ? d : e.message), { duration: 8000 });
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!templateId) { toast.error("Pick a template"); return; }
    const cleanBullets = bullets.map((b) => b.trim()).filter(Boolean);
    try {
      const payload = {
        campaign_id: campaignId,
        template_id: templateId,
        name: name || "Statement",
        kind: "statement",
        headline, accent, bullets: cleanBullets,
        type_speed: typeSpeed,
        marketing_message: marketingMessage,
      };
      if (variation?.id) await api.patch(`/variations/${variation.id}`, payload);
      else await api.post("/variations", payload);
      toast.success("Saved");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const updateBullet = (i, val) => setBullets((b) => b.map((x, k) => (k === i ? val : x)));
  const removeBullet = (i) => setBullets((b) => b.filter((_, k) => k !== i));
  const move = (i, dir) => setBullets((b) => {
    const next = [...b]; const j = i + dir;
    if (j < 0 || j >= next.length) return next;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  return (
    <div className="fixed inset-0 z-50 bg-gray-900/60 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-start sm:items-center justify-center p-4">
        <div className="w-full max-w-6xl bg-white rounded-2xl grid grid-cols-1 md:grid-cols-5 relative overflow-hidden my-4 max-h-[calc(100vh-2rem)]">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 z-10">
            <X className="h-5 w-5" />
          </button>

          {/* Preview */}
          <div className="md:col-span-2 p-6 border-r border-gray-100 bg-gray-50">
            <p className="text-xs font-medium text-gray-500 mb-2">Preview</p>
            <StatementPreview
              template={template} headline={headline} accent={accent} bullets={bullets}
              typeSpeed={typeSpeed} animate
            />
          </div>

          {/* Editor */}
          <div className="md:col-span-3 p-6 space-y-4 overflow-y-auto max-h-[85vh]">
            <div>
              <h2 className="font-lexend text-2xl text-gray-900">
                {variation?.id ? "Edit statement" : "New statement card"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">Headline + accent phrase → a typed-in checklist.</p>
            </div>

            {/* AI generator */}
            <div className="bg-brand-50 border border-brand-200 rounded-xl p-4">
              <label className="block text-xs font-semibold text-brand-700 mb-2">Marketing message</label>
              <textarea
                rows={2}
                placeholder='e.g. "Nine specialized AI agents review every change."'
                value={marketingMessage}
                onChange={(e) => setMarketingMessage(e.target.value)}
                maxLength={4000}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
              />
              <div className="flex justify-end mt-3">
                <button onClick={generate} disabled={busy}
                  className="btn-brand inline-flex items-center gap-1.5 px-3 py-1.5 text-xs disabled:opacity-40">
                  {busy ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Generating…</>
                        : <><Wand2 className="h-3.5 w-3.5" /> Generate statement</>}
                </button>
              </div>
            </div>

            {/* Name + Template + Type speed */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Template</label>
                <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white">
                  {statementTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Type speed</label>
                <select value={typeSpeed} onChange={(e) => setTypeSpeed(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white">
                  <option value="slow">slow</option>
                  <option value="medium">medium</option>
                  <option value="fast">fast</option>
                </select>
              </div>
            </div>

            {/* Headline + accent */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Headline</label>
              <input value={headline} onChange={(e) => setHeadline(e.target.value)}
                placeholder="Nine specialized AI agents."
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Accent phrase <span className="text-gray-400 font-normal">— colored orange (must appear in the headline)</span>
              </label>
              <input value={accent} onChange={(e) => setAccent(e.target.value)}
                placeholder="AI"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
            </div>

            {/* Bullets */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-medium text-gray-700">Checklist bullets <span className="text-gray-400 font-normal">· 1–2 lines each</span></label>
                <button onClick={() => setBullets((b) => [...b, ""])}
                  className="text-[11px] rounded border border-gray-200 hover:border-brand hover:text-brand px-2 py-1 text-gray-600 inline-flex items-center gap-1">
                  <Plus className="h-3 w-3" /> bullet
                </button>
              </div>
              <div className="space-y-1.5">
                {bullets.map((b, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-2 py-1.5">
                    <div className="flex flex-col">
                      <button onClick={() => move(i, -1)} className="text-gray-400 hover:text-gray-900 text-[8px]">▲</button>
                      <button onClick={() => move(i, +1)} className="text-gray-400 hover:text-gray-900 text-[8px]">▼</button>
                    </div>
                    <input value={b} onChange={(e) => updateBullet(i, e.target.value)}
                      placeholder={`Bullet ${i + 1}`}
                      className="flex-1 text-sm rounded border border-gray-200 bg-white px-2 py-1" />
                    <button onClick={() => removeBullet(i)} className="text-gray-300 hover:text-red-500">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {bullets.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-3">No bullets — the card shows just the headline.</p>
                )}
              </div>
            </div>

            <div className="pt-2 flex justify-end gap-2">
              <button onClick={onClose}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={save} className="btn-brand inline-flex items-center gap-2 px-5 py-2 text-sm">
                <Sparkles className="h-4 w-4" /> Save statement
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
